import { DEFAULT_CONNECT_TIMEOUT, DEFAULT_MESSAGE_TIMEOUT } from './constants';
import type { ParseResult, WebSocketClientOptions } from './types';
import { WebSocketError } from './WebSocketError';

export class WebSocketClient {
  private readonly url: string;
  private readonly connectTimeout: number;
  private readonly messageTimeout: number;

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.connectTimeout = options.connectTimeout ?? DEFAULT_CONNECT_TIMEOUT;
    this.messageTimeout = options.messageTimeout ?? DEFAULT_MESSAGE_TIMEOUT;
  }

  // token の refresh は auth の session 側で行われるため、接続失敗時に front から refresh しない。
  async connect(): Promise<WebSocket> {
    return await this.openSocket();
  }

  async sendAndReceive<T>(
    payload: Record<string, unknown>,
    options?: {
      timeout?: number;
      parseMessage?: (raw: unknown) => ParseResult<T>;
      skipMessage?: (raw: unknown) => boolean;
    },
  ): Promise<T> {
    if (!this.url) {
      throw new WebSocketError('WebSocket URL not configured', 'CONNECT_FAILED', 0);
    }

    const ws = await this.connect();
    const timeout = options?.timeout ?? this.messageTimeout;

    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (fn: () => void) => {
        if (!settled) {
          settled = true;
          fn();
        }
      };

      const armTimer = () =>
        setTimeout(() => {
          ws.close();
          settle(() => reject(new WebSocketError('タイムアウトしました', 'TIMEOUT', 0)));
        }, timeout);

      let timer = armTimer();

      ws.send(JSON.stringify(payload));

      ws.onmessage = (e) => {
        try {
          const raw = JSON.parse(e.data as string);

          if (options?.skipMessage?.(raw)) {
            clearTimeout(timer);
            timer = armTimer();
            return;
          }

          clearTimeout(timer);
          ws.close();

          if (options?.parseMessage) {
            const result = options.parseMessage(raw);
            if (result.ok) {
              settle(() => resolve(result.data));
            } else {
              settle(() => reject(result.error));
            }
          } else {
            const msg = raw as { event: string; data: unknown };
            if (msg.event === 'error') {
              const err = msg.data as { message: string };
              settle(() => reject(new WebSocketError(err.message, 'SERVER_ERROR', 0)));
            } else {
              settle(() => resolve(msg.data as T));
            }
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {
        clearTimeout(timer);
        settle(() => reject(new WebSocketError('ネットワークに接続できません', 'NETWORK_ERROR', 0)));
      };

      ws.onclose = (e) => {
        clearTimeout(timer);
        if (e.code !== 1000 && e.code !== 1005) {
          settle(() => reject(new WebSocketError('接続が切断されました', 'DISCONNECTED', 0)));
        }
      };
    });
  }

  /**
   * 1リクエストの応答が複数フレームで届く場合に使う。受信したフレームを **そのまま配列で** 返す。
   *
   * API Gateway WebSocket は 1 フレーム 128KB が上限で、超えると PostToConnection が 413 で失敗する。
   * 長い会話履歴 (thread_messages) はサーバー側で分割して push されるため、`isDone` が true を返す
   * フレームまで受信する。フレームの結合はドメイン側の責務であり、transport はここでは行わない
   * （結合処理の例外が socket の lifecycle に影響しないようにするため）。
   *
   * タイムアウトはフレーム受信ごとにリセットされる（リクエスト全体ではなくフレーム間の idle timeout）。
   *
   * 完了フレームを受け取る前に socket が閉じた場合は、close code が 1000/1005 であっても
   * DISCONNECTED で reject する: 履歴が不完全なため resolve できず、settle しなければ Promise が
   * 永久に pending になる。settled フラグは sendAndStream と同じ意図で、resolve/reject 後に
   * close/error が届いても二重 settle しないためのもの。
   */
  async sendAndReceiveMany<T>(
    payload: Record<string, unknown>,
    isDone: (frame: T) => boolean,
    options?: { timeout?: number },
  ): Promise<T[]> {
    if (!this.url) {
      throw new WebSocketError('WebSocket URL not configured', 'CONNECT_FAILED', 0);
    }

    const ws = await this.connect();
    const timeout = options?.timeout ?? this.connectTimeout;

    return new Promise((resolve, reject) => {
      const frames: T[] = [];
      let settled = false;
      let timer: ReturnType<typeof setTimeout>;

      // settle が timer の解除も担当する。これにより「timer を解除したのに settle しない」経路を
      // 作れない。resolve/reject を先に確定させてから close するので、close が同期的に onclose を
      // 呼ぶ実装でも DISCONNECTED で上書きされない。
      const settle = (fn: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        fn();
      };
      const armTimer = () => {
        timer = setTimeout(() => {
          settle(() => reject(new WebSocketError('タイムアウトしました', 'TIMEOUT', 0)));
          ws.close();
        }, timeout);
      };
      armTimer();

      ws.send(JSON.stringify(payload));

      ws.onmessage = (e) => {
        if (settled) {
          return; // resolve 済みの frames 配列は呼び出し側が保持しているので、後続フレームで変更しない
        }
        let raw: { event: string; data: unknown };
        try {
          raw = JSON.parse(e.data as string) as { event: string; data: unknown };
        } catch {
          return; // ignore malformed messages — idle timeout は有効なままなので pending にならない
        }

        if (raw.event === 'error') {
          const err = raw.data as { message: string };
          settle(() => reject(new WebSocketError(err.message, 'SERVER_ERROR', 0)));
          ws.close();
          return;
        }

        clearTimeout(timer);
        const frame = raw.data as T;
        frames.push(frame);

        let done: boolean;
        try {
          done = isDone(frame);
        } catch {
          // 判定が throw しても timer を解除したまま放置しない。
          settle(() => reject(new WebSocketError('応答の解析に失敗しました', 'SERVER_ERROR', 0)));
          ws.close();
          return;
        }

        if (done) {
          settle(() => resolve(frames));
          ws.close();
          return;
        }
        armTimer();
      };

      ws.onerror = () => {
        settle(() => reject(new WebSocketError('ネットワークに接続できません', 'NETWORK_ERROR', 0)));
      };

      ws.onclose = () => {
        settle(() =>
          reject(new WebSocketError('接続が切断されました。再試行してください。', 'DISCONNECTED', 0)),
        );
      };
    });
  }

  async sendAndStream(
    payload: Record<string, unknown>,
    onMessage: (raw: { event: string; data: Record<string, unknown> }) => void,
    options?: { timeout?: number; signal?: AbortSignal; isTerminal?: (event: string) => boolean },
  ): Promise<void> {
    if (!this.url) {
      throw new WebSocketError('WebSocket URL not configured', 'CONNECT_FAILED', 0);
    }

    const ws = await this.connect();
    const timeout = options?.timeout ?? this.messageTimeout;
    const isTerminal = options?.isTerminal ?? ((event: string) => event === 'run_end');

    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (fn: () => void) => {
        if (!settled) {
          settled = true;
          fn();
        }
      };

      const timer = setTimeout(() => {
        ws.close();
        settle(() =>
          reject(
            new WebSocketError(
              '応答に時間がかかっています。再試行するか、後でもう一度お試しください。',
              'TIMEOUT',
              0,
            ),
          ),
        );
      }, timeout);

      if (options?.signal) {
        options.signal.addEventListener('abort', () => {
          clearTimeout(timer);
          ws.close();
          settle(() => resolve());
        });
      }

      ws.send(JSON.stringify(payload));

      ws.onmessage = (e) => {
        try {
          const raw = JSON.parse(e.data as string) as {
            event: string;
            data: Record<string, unknown>;
          };
          onMessage(raw);

          if (raw.event === 'error') {
            clearTimeout(timer);
            ws.close();
            const message = (raw.data.message as string) || '';
            const code = raw.data.code as string | undefined;
            const statusCode = code === 'AUTH_ERROR' ? 401 : code === 'TOKEN_NOT_FOUND' ? 404 : 503;
            settle(() => reject(new WebSocketError(message, 'SERVER_ERROR', statusCode)));
            return;
          }

          if (isTerminal(raw.event)) {
            clearTimeout(timer);
            ws.close();
            settle(() => resolve());
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {
        clearTimeout(timer);
        settle(() =>
          reject(
            new WebSocketError(
              'ネットワークに接続できません。インターネット接続を確認してください。',
              'NETWORK_ERROR',
              0,
            ),
          ),
        );
      };

      ws.onclose = (e) => {
        clearTimeout(timer);
        if (e.code === 1000 || e.code === 1005) {
          settle(() => resolve());
        } else {
          settle(() =>
            reject(new WebSocketError('接続が切断されました。再試行してください。', 'DISCONNECTED', 0)),
          );
        }
      };
    });
  }

  private openSocket(): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);

      const timer = setTimeout(() => {
        cleanup();
        ws.close();
        reject(new WebSocketError('接続がタイムアウトしました', 'TIMEOUT', 0));
      }, this.connectTimeout);

      const onOpen = () => {
        cleanup();
        resolve(ws);
      };
      const onError = () => {
        cleanup();
        reject(new WebSocketError('WebSocket接続に失敗しました', 'CONNECT_FAILED', 0));
      };
      const onClose = (e: CloseEvent) => {
        cleanup();
        if (e.code === 1000 || e.code === 1005) {
          resolve(ws);
        } else {
          reject(new WebSocketError(`ws_close_${e.code}`, 'CONNECT_FAILED', 0));
        }
      };
      const cleanup = () => {
        clearTimeout(timer);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        ws.removeEventListener('close', onClose);
      };

      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
      ws.addEventListener('close', onClose);
    });
  }
}
